import {
  KnowledgeDocumentFacet,
  KnowledgeDocumentListItem,
  marketMatchesFilter,
} from '@digital-twins/shared-types'
import i18n, { intlLocaleForUi } from '@/i18n'
import { COUNTRIES } from '@/lib/constants/demographics'
import { localizedCountryName } from '@/lib/utils/localized-country-name'
import { isKnowledgeVideoUrl } from './videoUrl'

const COVER_COUNT = 50

export function coverBackgroundForToken(token: string): string {
  let hash = 0
  for (let i = 0; i < token.length; i++) {
    hash = (hash * 31 + token.charCodeAt(i)) >>> 0
  }
  const index = (hash % COVER_COUNT) + 1
  const padded = String(index).padStart(2, '0')
  return `/cover_backgrounds/cover_background_${padded}.svg`
}

/**
 * Library card cover: persisted first-page URL, then link OG thumb, then SVG.
 * Never triggers on-demand rasterization — that path is too slow for the grid.
 */
export function documentCoverUrl(doc: KnowledgeDocumentListItem): string {
  const cover = doc.coverImageUrl?.trim()
  if (cover) {
    return cover
  }
  const linkThumb = doc.linkThumbnailUrl?.trim()
  if (linkThumb) {
    return linkThumb
  }
  return coverBackgroundForToken(doc.token)
}

export function facetValue(
  facets: Array<KnowledgeDocumentFacet> | undefined,
  kind: string,
): string | null {
  const match = (facets ?? []).find((f) => f.kind === kind && !f.rejected)
  return match?.value?.trim() || null
}

/**
 * All non-rejected values for a facet kind, trimmed, empties dropped.
 * Use this over `facetValue` when a document can carry more than one
 * value for a kind (e.g. multiple markets, multiple methods).
 */
export function facetValues(
  facets: Array<KnowledgeDocumentFacet> | undefined,
  kind: string,
): Array<string> {
  return (facets ?? [])
    .filter((f) => f.kind === kind && !f.rejected)
    .map((f) => f.value?.trim() || '')
    .filter(Boolean)
}

export function documentTypeLabel(doc: KnowledgeDocumentListItem): string {
  if (doc.isSimulationArtifact) {
    return 'Simulation report'
  }
  const fromFacet = facetValue(doc.facets, 'type')
  if (fromFacet) {
    return fromFacet
  }
  const mime = (doc.mimeType || '').toLowerCase()
  if (mime.includes('pdf')) return 'Report'
  if (mime.includes('presentation') || mime.includes('powerpoint')) return 'Deck'
  if (mime.includes('sheet') || mime.includes('excel') || mime.includes('csv')) {
    return 'Data'
  }
  if (mime.startsWith('video/')) return 'Video'
  if (mime.startsWith('audio/')) return 'Audio'
  if (doc.sourceUrl && isKnowledgeVideoUrl(doc.sourceUrl)) return 'Video'
  if (doc.sourceUrl) return 'Link'
  return 'Document'
}

/** Stored English type (or mime fallback) → UI chrome label. Unknown types stay as stored. */
const KNOWLEDGE_TYPE_I18N_KEYS: Record<string, string> = {
  'Simulation report': 'library.type.simulationReport',
  Report: 'library.type.report',
  Deck: 'library.type.deck',
  Data: 'library.type.data',
  Video: 'library.type.video',
  Audio: 'library.type.audio',
  Link: 'library.type.link',
  Document: 'library.type.document',
  'Market Report': 'library.type.marketReport',
  'Value Chain Analysis': 'library.type.valueChainAnalysis',
  Guide: 'library.type.guide',
  'Academic Article': 'library.type.academicArticle',
  'Academic Thesis': 'library.type.academicThesis',
  'Business Advice': 'library.type.businessAdvice',
  'Business Plan': 'library.type.businessPlan',
  'Business Update': 'library.type.businessUpdate',
  'Technical Note': 'library.type.technicalNote',
  'Technical Paper': 'library.type.technicalPaper',
  'Research Study': 'library.type.researchStudy',
  'Organizational Overview': 'library.type.organizationalOverview',
}

const FACET_ALL_I18N_KEYS: Record<string, string> = {
  market: 'library.allMarket',
  method: 'library.allMethod',
  year: 'library.allYear',
  source: 'library.allSource',
  category: 'library.allCategory',
  brand: 'library.allBrand',
  type: 'library.allType',
  owner: 'library.allOwner',
}

export function displayKnowledgeTypeLabel(stored: string): string {
  const key = KNOWLEDGE_TYPE_I18N_KEYS[stored]
  return key ? i18n.t(key, { ns: 'app' }) : stored
}

export function knowledgeFacetKindLabel(kind: string): string {
  const key = `library.facet.${kind}`
  const translated = i18n.t(key, { ns: 'app' })
  return translated === key ? kind : translated
}

export function knowledgeFacetAllLabel(kind: string): string {
  const key = FACET_ALL_I18N_KEYS[kind]
  return key ? i18n.t(key, { ns: 'app' }) : i18n.t('library.all', { ns: 'app' })
}

/** Short file-kind token for card footers / doc meta (PDF · DOCX · …). */
export function fileKindLabel(doc: KnowledgeDocumentListItem): string {
  if (doc.isSimulationArtifact) return 'SIM'
  if (doc.sourceUrl && isKnowledgeVideoUrl(doc.sourceUrl)) return 'VIDEO'
  if (doc.sourceUrl) return 'LINK'
  const mime = (doc.mimeType || '').toLowerCase()
  const name = (doc.originalFilename || '').toLowerCase()
  if (mime.includes('pdf') || name.endsWith('.pdf')) return 'PDF'
  // `document` alone would also match the OOXML spreadsheet/presentation mimes,
  // which all contain `officedocument`.
  if (
    mime.includes('word') ||
    mime.includes('opendocument.text') ||
    name.endsWith('.docx') ||
    name.endsWith('.doc')
  ) {
    return 'DOCX'
  }
  if (
    mime.includes('presentation') ||
    mime.includes('powerpoint') ||
    name.endsWith('.pptx') ||
    name.endsWith('.ppt')
  ) {
    return 'PPTX'
  }
  if (
    mime.includes('sheet') ||
    mime.includes('excel') ||
    mime.includes('csv') ||
    name.endsWith('.xlsx') ||
    name.endsWith('.xls') ||
    name.endsWith('.csv')
  ) {
    return 'DATA'
  }
  if (mime.startsWith('video/')) return 'VIDEO'
  if (mime.startsWith('audio/') || name.endsWith('.mp3') || name.endsWith('.wav') || name.endsWith('.m4a')) {
    return 'AUDIO'
  }
  return documentTypeLabel(doc).toUpperCase()
}

export function humanizeDocumentFilename(
  name: string | null | undefined,
): string {
  const raw = (name || '').trim()
  if (!raw) {
    return i18n.t('library.untitled', { ns: 'app' })
  }
  const withoutExt = raw.replace(/\.[a-z0-9]{1,8}$/i, '')
  if (/^(https?:\/\/|www\.)/i.test(withoutExt)) {
    const host = withoutExt
      .replace(/^https?:\/\//i, '')
      .replace(/^www\./i, '')
      .split(/[/?#]/)[0]
      .split('.')[0]
      .replace(/[-_]+/g, ' ')
      .trim()
    if (host) {
      return host.charAt(0).toUpperCase() + host.slice(1)
    }
  }
  const spaced = withoutExt.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim()
  return spaced || raw
}

function looksLikeUrlTitle(value: string): boolean {
  const t = value.trim().toLowerCase()
  if (!t) {
    return false
  }
  return (
    t.startsWith('http://') ||
    t.startsWith('https://') ||
    /^www\.[a-z0-9.-]+$/i.test(t)
  )
}

function derivedTitleFromSummary(summary: string): string {
  const first =
    summary.split(/(?<=[.!?])\s/)[0]?.trim() || summary.trim()
  if (first.length <= 72) {
    return first.replace(/[.]+$/, '')
  }
  return `${first.slice(0, 69).trimEnd()}…`
}

export function documentTitle(doc: KnowledgeDocumentListItem): string {
  const titled = doc.title?.trim()
  if (titled) {
    return titled
  }
  const linkTitle = doc.linkTitle?.trim()
  if (linkTitle && !looksLikeUrlTitle(linkTitle)) {
    return linkTitle
  }
  const summary = doc.summary?.trim()
  if (summary) {
    return derivedTitleFromSummary(summary)
  }
  return humanizeDocumentFilename(doc.originalFilename)
}

export function documentInsight(doc: KnowledgeDocumentListItem): string {
  return documentDescription(doc) || documentTitle(doc)
}

/** Card body copy: model summary, else extracted excerpt, else OG description. */
export function documentDescription(
  doc: KnowledgeDocumentListItem,
): string | null {
  const summary = doc.summary?.trim()
  const excerpt = doc.excerpt?.trim()
  const linkDesc = doc.linkDescription?.trim()
  const candidate = summary || excerpt || linkDesc || null
  if (!candidate) {
    return null
  }
  const title = documentTitle(doc)
  const normalize = (s: string) => s.replace(/[.]+$/g, '').trim().toLowerCase()
  if (normalize(candidate) === normalize(title)) {
    return null
  }
  return candidate
}

const ASK_PREFIX =
  /^(what|why|how|when|where|who|which|does|do|did|is|are|can|could|should|will|would|i want|i need|i'd like|id like|tell me|show me|find me|find something|find out|looking for|explain|summarise|summarize|help me|please|give me|i am looking|i'm looking|im looking)\b/

/** True when the library query is an ask (Enter → Threads), not a keyword search. */
export function isQuestionShaped(query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return false
  if (q.includes('?')) return true
  return ASK_PREFIX.test(q)
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** Split findings markdown into numbered list items when it looks bulletized. */
export function parseFindingsItems(markdown: string | null | undefined): Array<string> {
  const md = markdown?.trim()
  if (!md) return []

  const lines = md.split(/\r?\n/)
  const bulletLines = lines
    .map((line) => line.trim())
    .filter((line) => /^([-*•]|\d+[.)])\s+/.test(line))
    .map((line) => line.replace(/^([-*•]|\d+[.)])\s+/, '').trim())
    .filter(Boolean)

  if (bulletLines.length >= 2) {
    return bulletLines
  }

  if (md.includes('\n') && lines.filter((l) => l.trim()).length >= 2) {
    return lines.map((l) => l.trim()).filter(Boolean)
  }

  return []
}

export const LIBRARY_FACET_QUERY_KEYS = [
  'market',
  'year',
  'method',
  'source',
  'category',
  'brand',
  'type',
  'owner',
] as const

export function filtersFromSearchParams(
  params: URLSearchParams,
): Record<string, string | null> {
  const next: Record<string, string | null> = {}
  for (const key of LIBRARY_FACET_QUERY_KEYS) {
    const value = params.get(key)
    if (value) next[key] = value
  }
  return next
}

export function documentMatchesFacetFilters(
  doc: KnowledgeDocumentListItem,
  filters: Record<string, string | null>,
): boolean {
  for (const [kind, value] of Object.entries(filters)) {
    if (!value) continue
    if (value === '__unknown__') {
      if (kind === 'owner') {
        if (doc.ownerName || doc.ownerToken) return false
        continue
      }
      if (facetValue(doc.facets, kind)) return false
      continue
    }
    if (kind === 'owner') {
      const owner = (doc.ownerName || doc.ownerToken || '').toLowerCase()
      if (!owner || owner !== value.toLowerCase()) return false
      continue
    }
    if (kind === 'type') {
      if (documentTypeLabel(doc).toLowerCase() !== value.toLowerCase()) {
        return false
      }
      continue
    }
    if (kind === 'market') {
      const markets = facetValues(doc.facets, 'market')
      const matchesAnyMarket = markets.some((market) =>
        marketMatchesFilter(market, value),
      )
      if (!matchesAnyMarket) {
        return false
      }
      continue
    }
    const actual = facetValue(doc.facets, kind)
    if (!actual || actual.toLowerCase() !== value.toLowerCase()) {
      return false
    }
  }
  return true
}

/**
 * Code -> English display name, derived from the same `COUNTRIES` list used
 * for audience creation (`@/lib/constants/demographics`). Do not hand-maintain
 * a second country list here.
 */
const COUNTRY_NAME_BY_CODE: Record<string, string> = Object.fromEntries(
  COUNTRIES.map((country) => [country.code, country.name]),
)

const COUNTRY_CODE_BY_NAME: Record<string, string> = Object.fromEntries(
  COUNTRIES.map((country) => [country.name.toLowerCase(), country.code]),
)

/**
 * Non-ISO market values that still reach us from older extractions and from
 * hand corrections. Deliberately tiny: anything already resolvable from
 * `COUNTRIES` (by code or by English name) must not be listed here.
 */
const MARKET_CODE_ALIASES: Record<string, string> = {
  UK: 'GB',
  USA: 'US',
}

/**
 * Stored market value -> ISO alpha-2 country code, or null when nothing
 * country-shaped is behind it ("Global", a region like "EMEA", free text).
 *
 * Handles the three forms the facet extractor and hand corrections produce:
 * an alpha-2 code ("IT"), an English country name ("United States"), and a
 * subdivision code ("GB-ENG"), which resolves to its parent country.
 */
function marketCountryCode(value: string): string | null {
  const raw = value.trim()
  if (!raw) {
    return null
  }
  const head = raw.toUpperCase().split('-')[0]
  if (COUNTRY_NAME_BY_CODE[head]) {
    return head
  }
  return MARKET_CODE_ALIASES[head] ?? COUNTRY_CODE_BY_NAME[raw.toLowerCase()] ?? null
}

/**
 * Resolve a stored market facet value (an ISO alpha-2 code, e.g. "US") to a
 * UI-language country name for rendering. Aliases ("UK") and subdivision
 * codes ("GB-ENG") resolve to their country too, so a reader never sees a
 * raw code next to a resolved name.
 *
 * Falls back to returning `value` unchanged when there is no match. This
 * fallback is load-bearing: it keeps legacy hand-typed name-form facet
 * values, region rollups ("EMEA"), and unmapped strings rendering correctly.
 * "Global" is product chrome and follows the UI language.
 *
 * Display-only: matching (`marketMatchesFilter`) keeps operating on the
 * stored value and must not be fed this resolved name.
 */
export function marketDisplayName(value: string, locale?: string): string {
  if (isGlobalMarket(value)) {
    return i18n.t('library.global', { ns: 'app' })
  }
  const code = marketCountryCode(value)
  if (code) {
    return localizedCountryName(code, locale ?? i18n.language)
  }
  return value
}

/** True for the catch-all "Global" market value, in any casing. */
export function isGlobalMarket(value: string): boolean {
  return value.trim().toLowerCase() === 'global'
}

/**
 * Regional-indicator flag emoji for a stored market value, or null when the
 * value has no single country behind it ("Global", a region, free text).
 *
 * Display-only, same contract as `marketDisplayName`.
 */
export function marketFlagEmoji(value: string): string | null {
  const code = marketCountryCode(value)
  if (!code) {
    return null
  }
  return String.fromCodePoint(
    ...Array.from(code).map((letter) => 0x1f1e6 + letter.charCodeAt(0) - 65),
  )
}

export interface MarketDisplayEntry {
  label: string
  flag: string | null
}

/**
 * Resolved, de-duplicated markets for display. Values that land on the same
 * country ("UK", "GB-ENG", "GB-WLS", "GB-SCT") collapse into one entry, so a
 * card counts the markets a reader would count rather than the rows stored.
 */
export function marketDisplayEntries(
  values: Array<string>,
): Array<MarketDisplayEntry> {
  const seen = new Set<string>()
  const entries: Array<MarketDisplayEntry> = []
  for (const value of values) {
    const label = marketDisplayName(value)
    const key = label.toLowerCase()
    if (seen.has(key)) {
      continue
    }
    seen.add(key)
    entries.push({ label, flag: marketFlagEmoji(value) })
  }
  return entries
}

export function formatLibraryDate(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return '—'
  }
  return date.toLocaleDateString(intlLocaleForUi(i18n.language), {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export function formatLibraryDateTime(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return '—'
  }
  return date.toLocaleString(intlLocaleForUi(i18n.language), {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function facetConfidenceLabel(
  confidence: number | null | undefined,
): string | null {
  if (confidence == null || Number.isNaN(confidence)) return null
  if (confidence >= 0.75) return i18n.t('library.confidenceHigh', { ns: 'app' })
  if (confidence >= 0.45) return i18n.t('library.confidenceMedium', { ns: 'app' })
  return i18n.t('library.confidenceLow', { ns: 'app' })
}
